/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState } from "react";
import { Plus, Sparkles, Search, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { API_ENDPOINTS } from "@/api/endpoints";
import { usePlayersAdmin } from "@/hooks/api/usePlayers";
import { toast } from "react-toastify";
import type { Player } from "@/types/api.types";

interface CellAnswer {
  id: number;
  playerId: number;
  player: {
    id: number;
    name: string;
    photo: string | null;
    position: string;
  };
}

interface CellAnswersResponse {
  cell: { id: number; row: number; col: number; rowTeam: string; colTeam: string };
  grid: { id: number; fromYear: number | null; toYear: number | null };
  answers: CellAnswer[];
}

interface SuggestResponse {
  rowTeam: string;
  colTeam: string;
  existingAnswerIds: number[];
  suggestions: {
    id: number;
    name: string;
    photo: string | null;
    position: string;
  }[];
}

interface Props {
  open: boolean;
  cellId: number | null;
  onOpenChange: (open: boolean) => void;
}

export default function TicTacToeCellAnswersDialog({
  open,
  cellId,
  onOpenChange,
}: Props) {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");

  const { data, isLoading } = useQuery<CellAnswersResponse>({
    queryKey: ["ttt", "cell-answers", cellId],
    queryFn: () =>
      apiClient.get(API_ENDPOINTS.QUIZ.TTT_CELL_ANSWERS(cellId as number)),
    enabled: open && !!cellId,
  });

  const {
    data: suggestData,
    isLoading: isSuggestLoading,
    refetch: refetchSuggest,
    isFetching: isSuggestFetching,
  } = useQuery<SuggestResponse>({
    queryKey: ["ttt", "cell-suggest", cellId],
    queryFn: () =>
      apiClient.get(API_ENDPOINTS.QUIZ.TTT_CELL_SUGGEST(cellId as number)),
    enabled: false,
  });

  // Include inactive players (e.g. retired legends) so they remain valid
  // tic-tac-toe answers — the fantasy filter only applies on the mobile
  // lineup picker, not admin quiz pickers.
  const { data: playerSearchData } = usePlayersAdmin(
    search.trim()
      ? { search: search.trim(), limit: 10, isActive: 'all' }
      : undefined,
  );

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["ttt", "cell-answers", cellId] });
    qc.invalidateQueries({ queryKey: ["quiz", "tictactoe"] });
  };

  const addMutation = useMutation({
    mutationFn: (playerIds: number[]) =>
      apiClient.post(API_ENDPOINTS.QUIZ.TTT_CELL_ANSWERS(cellId as number), {
        playerIds,
      }),
    onSuccess: () => {
      toast.success("Answer(s) added");
      invalidate();
      setSearch("");
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.message || "Failed to add"),
  });

  const removeMutation = useMutation({
    mutationFn: (playerId: number) =>
      apiClient.delete(
        API_ENDPOINTS.QUIZ.TTT_CELL_ANSWER_REMOVE(cellId as number, playerId),
      ),
    onSuccess: () => {
      toast.success("Removed");
      invalidate();
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.message || "Failed to remove"),
  });

  const existingPlayerIds = useMemo(
    () => new Set((data?.answers ?? []).map((a) => a.playerId)),
    [data?.answers],
  );

  const searchResults: Player[] = playerSearchData?.players ?? [];

  const handleAddOne = (playerId: number) => {
    if (existingPlayerIds.has(playerId)) return;
    addMutation.mutate([playerId]);
  };

  const handleAddAllSuggestions = () => {
    if (!suggestData) return;
    const newIds = suggestData.suggestions
      .map((s) => s.id)
      .filter((id) => !existingPlayerIds.has(id));
    if (newIds.length === 0) {
      toast.info("All suggested players are already added");
      return;
    }
    addMutation.mutate(newIds);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {data ? (
              <>
                Answers for{" "}
                <span className="text-indigo-600">
                  {data.cell.rowTeam} × {data.cell.colTeam}
                </span>
              </>
            ) : (
              "Cell Answers"
            )}
          </DialogTitle>
          {data?.grid && (data.grid.fromYear || data.grid.toYear) ? (
            <p className="text-xs text-gray-500 mt-1">
              Era: {data.grid.fromYear ?? "—"} to {data.grid.toYear ?? "present"}
            </p>
          ) : null}
        </DialogHeader>

        {isLoading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-500" />
          </div>
        ) : (
          <div className="space-y-5 py-2">
            {/* Auto-suggest panel */}
            <section className="border rounded-lg p-3 bg-indigo-50/30">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                    <Sparkles className="h-4 w-4 text-indigo-500" />
                    Suggest from career data
                  </p>
                  <p className="text-xs text-gray-500">
                    Players whose career covers both clubs within the grid era.
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refetchSuggest()}
                  disabled={isSuggestFetching}
                >
                  {isSuggestFetching ? "Loading..." : suggestData ? "Refresh" : "Run"}
                </Button>
              </div>

              {isSuggestLoading ? (
                <div className="text-sm text-gray-500 py-2">Loading...</div>
              ) : suggestData ? (
                suggestData.suggestions.length === 0 ? (
                  <p className="text-sm text-gray-500 py-2">
                    No players in career data match both clubs + era. Add career
                    entries in the Player Career page first, or add manually below.
                  </p>
                ) : (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs text-gray-600">
                        {suggestData.suggestions.length} suggestion
                        {suggestData.suggestions.length !== 1 ? "s" : ""}
                      </p>
                      <Button
                        size="sm"
                        onClick={handleAddAllSuggestions}
                        disabled={addMutation.isPending}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white h-7 text-xs"
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Add all new
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {suggestData.suggestions.map((p) => {
                        const already = existingPlayerIds.has(p.id);
                        return (
                          <Button
                            key={p.id}
                            variant={already ? "secondary" : "outline"}
                            size="sm"
                            disabled={already || addMutation.isPending}
                            onClick={() => handleAddOne(p.id)}
                            className="h-7 text-xs"
                          >
                            {already ? "✓ " : "+ "}
                            {p.name}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                )
              ) : null}
            </section>

            {/* Manual search panel */}
            <section>
              <p className="text-sm font-semibold text-gray-800 mb-2">
                Add a specific player
              </p>
              <div className="relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search players..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              {search.trim() && searchResults.length > 0 ? (
                <div className="mt-2 border rounded-lg overflow-hidden">
                  {searchResults.map((p) => {
                    const already = existingPlayerIds.has(p.id);
                    return (
                      <div
                        key={p.id}
                        className="flex items-center justify-between px-3 py-2 border-b last:border-b-0 hover:bg-gray-50"
                      >
                        <div className="flex items-center gap-2">
                          {p.photo ? (
                            <img
                              src={p.photo}
                              alt=""
                              className="w-6 h-6 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-gray-200" />
                          )}
                          <span className="text-sm">{p.name}</span>
                          <Badge className="bg-gray-100 text-gray-600 text-xs">
                            {p.position}
                          </Badge>
                        </div>
                        <Button
                          size="sm"
                          variant={already ? "secondary" : "outline"}
                          disabled={already || addMutation.isPending}
                          onClick={() => handleAddOne(p.id)}
                          className="h-7 text-xs"
                        >
                          {already ? "Added" : "+ Add"}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              ) : search.trim() ? (
                <p className="text-xs text-gray-500 mt-2">No players found.</p>
              ) : null}
            </section>

            {/* Current answers */}
            <section>
              <p className="text-sm font-semibold text-gray-800 mb-2">
                Current answers{" "}
                <Badge className="ml-1 bg-gray-100 text-gray-700">
                  {data?.answers.length ?? 0}
                </Badge>
              </p>
              {(data?.answers ?? []).length === 0 ? (
                <p className="text-xs text-gray-500 py-2">
                  No answers yet. Use suggestions or search to add.
                </p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {data?.answers.map((a) => (
                    <div
                      key={a.id}
                      className="flex items-center gap-1.5 bg-gray-100 rounded-full pl-2 pr-1 py-1 text-sm"
                    >
                      <span>{a.player.name}</span>
                      <button
                        onClick={() => removeMutation.mutate(a.playerId)}
                        disabled={removeMutation.isPending}
                        className="w-5 h-5 rounded-full bg-white hover:bg-red-100 flex items-center justify-center"
                        title="Remove"
                      >
                        <X className="h-3 w-3 text-red-500" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

